import React, { useState, useEffect, useCallback, useRef } from 'react';
import { consultationService, authService, patientService } from '../services/api';
import CIE10Selector from './CIE10Selector';
import RecetaPreview from './RecetaPreview';
import './PatientForm.css';

// Vital signs range definitions
const VITAL_RANGES = {
  peso: { min: 0.5, max: 300, unit: 'kg', label: 'Peso', normalMin: 40, normalMax: 120 },
  talla: { min: 0.3, max: 3.0, unit: 'm', label: 'Talla', normalMin: 1.4, normalMax: 2.0 },
  temperatura: { min: 34.0, max: 42.0, unit: '°C', label: 'Temperatura', normalMin: 36.0, normalMax: 37.5 },
  tension_arterial_sistolica: { min: 60, max: 300, unit: 'mmHg', label: 'T/A Sistólica', normalMin: 90, normalMax: 140 },
  tension_arterial_diastolica: { min: 30, max: 200, unit: 'mmHg', label: 'T/A Diastólica', normalMin: 60, normalMax: 90 },
  frecuencia_cardiaca: { min: 30, max: 250, unit: 'lpm', label: 'FC', normalMin: 60, normalMax: 100 },
  frecuencia_respiratoria: { min: 8, max: 60, unit: 'rpm', label: 'FR', normalMin: 12, normalMax: 20 },
};

const getVitalStatus = (field, value) => {
  if (!value || value === '') return 'empty';
  const num = Number(value);
  if (Number.isNaN(num)) return 'invalid';
  const range = VITAL_RANGES[field];
  if (!range) return 'normal';
  if (num < range.min || num > range.max) return 'impossible';
  if (num < range.normalMin || num > range.normalMax) return 'warning';
  return 'normal';
};

const createEmptyMedication = () => ({
  nombre: '',
  presentacion: '',
  dosis: '',
  via: '',
  duracion: '',
  cantidad: ''
});

const createInitialFormData = () => ({
  // 3.1 Ficha de ID + SV + antecedentes
  nombre: '',
  genero: '',
  fecha_nacimiento: '',
  ocupacion: '',
  domicilio: '',
  nombre_tutor: '',

  // Signos Vitales
  peso: '',
  talla: '',
  temperatura: '',
  tension_arterial_sistolica: '',
  tension_arterial_diastolica: '',
  frecuencia_cardiaca: '',
  frecuencia_respiratoria: '',

  // Campos adicionales 3.1
  aseguradora: '',
  medio_llegada: '',
  fecha_ingreso: '',

  // Antecedentes (fusionado en 3.1)
  antecedentes_familiares: '',
  antecedentes_personales_no_patologicos: '',
  antecedentes_personales_patologicos: '',
  antecedentes_gineco_obstetricos: '',

  // 3.3 - 3.8
  motivo_consulta: '',
  interrogatorio: '',
  exploracion_fisica: '',
  diagnostico_cie10: [],
  tratamiento: '',
  solicitar_laboratorio: false,
  solicitud_laboratorio: '',
  resultados_laboratorio: '',
  pronostico: '',
  idx_diagnostico_presuntivo: '',
  medicamentos: [createEmptyMedication()],
  receta_medicamento_controlado: '',
  receta_domicilio_controlado: ''
});

const buildDraftSessionKey = (consultationId) => `consultation_draft_${consultationId}`;

const hasMedicationData = (medication = {}) => (
  Object.values(medication).some((value) => String(value || '').trim())
);

const hasDraftPatientBasics = (formData) => (
  Boolean(formData.nombre?.trim() && formData.genero && formData.fecha_nacimiento)
);

const hasDraftableData = (formData, currentStep, selectedPatientId) => {
  if (selectedPatientId || currentStep > 1 || formData.solicitar_laboratorio) {
    return true;
  }

  if (Array.isArray(formData.diagnostico_cie10) && formData.diagnostico_cie10.length > 0) {
    return true;
  }

  if (Array.isArray(formData.medicamentos) && formData.medicamentos.some(hasMedicationData)) {
    return true;
  }

  return Object.entries(formData).some(([key, value]) => {
    if (['solicitar_laboratorio', 'diagnostico_cie10', 'medicamentos'].includes(key)) {
      return false;
    }

    return typeof value === 'string' && value.trim().length > 0;
  });
};

const formatAutosaveTimestamp = (value) => {
  if (!value) return '';
  return new Date(value).toLocaleTimeString('es-MX', {
    hour: '2-digit',
    minute: '2-digit'
  });
};

const normalizeLoadedFormData = (data = {}) => {
  const initialFormData = createInitialFormData();

  return {
    ...initialFormData,
    ...data,
    diagnostico_cie10: Array.isArray(data.diagnostico_cie10) ? data.diagnostico_cie10 : initialFormData.diagnostico_cie10,
    medicamentos: Array.isArray(data.medicamentos) && data.medicamentos.length > 0
      ? data.medicamentos
      : initialFormData.medicamentos,
    solicitar_laboratorio: Boolean(data.solicitar_laboratorio)
  };
};

const mapConsultationToFormData = (consulta = {}) => normalizeLoadedFormData({
  nombre: consulta.nombre || '',
  genero: consulta.genero || '',
  fecha_nacimiento: consulta.fecha_nacimiento ? String(consulta.fecha_nacimiento).slice(0, 10) : '',
  ocupacion: consulta.ocupacion || '',
  domicilio: consulta.domicilio || '',
  nombre_tutor: consulta.nombre_tutor || '',
  peso: consulta.peso ?? '',
  talla: consulta.talla ?? '',
  temperatura: consulta.temperatura ?? '',
  tension_arterial_sistolica: consulta.tension_arterial_sistolica ?? '',
  tension_arterial_diastolica: consulta.tension_arterial_diastolica ?? '',
  frecuencia_cardiaca: consulta.frecuencia_cardiaca ?? '',
  frecuencia_respiratoria: consulta.frecuencia_respiratoria ?? '',
  aseguradora: consulta.aseguradora || '',
  medio_llegada: consulta.medio_llegada || '',
  fecha_ingreso: consulta.fecha_ingreso ? String(consulta.fecha_ingreso).slice(0, 16) : '',
  antecedentes_familiares: consulta.antecedentes_familiares || '',
  antecedentes_personales_no_patologicos: consulta.antecedentes_personales_no_patologicos || '',
  antecedentes_personales_patologicos: consulta.antecedentes_personales_patologicos || '',
  antecedentes_gineco_obstetricos: consulta.antecedentes_gineco_obstetricos || '',
  fracturas: consulta.fracturas || '',
  enfermedades_previas: consulta.enfermedades_previas || '',
  hospitalizaciones_previas: consulta.hospitalizaciones_previas || '',
  fumas_o_tomas: consulta.fumas_o_tomas || '',
  adicciones: consulta.adicciones || '',
  motivo_consulta: consulta.motivo_consulta || '',
  interrogatorio: consulta.interrogatorio || '',
  exploracion_fisica: consulta.exploracion_fisica || '',
  diagnostico_cie10: Array.isArray(consulta.diagnostico_cie10) ? consulta.diagnostico_cie10 : [],
  tratamiento: consulta.tratamiento || '',
  solicitar_laboratorio: Boolean(consulta.solicitar_laboratorio),
  solicitud_laboratorio: consulta.solicitud_laboratorio || '',
  resultados_laboratorio: consulta.resultados_laboratorio || '',
  pronostico: consulta.pronostico || ''
});

const PatientForm = ({
  user,
  onBack,
  onSuccess,
  consultationId = null,
  draftSessionKey = null,
  onDraftsChanged = null,
  backLabel = 'Volver al Dashboard'
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [showReceta, setShowReceta] = useState(false);
  const [savedConsultation, setSavedConsultation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [isAutosaving, setIsAutosaving] = useState(false);
  const [autosaveStatus, setAutosaveStatus] = useState('idle');
  const [lastAutosavedAt, setLastAutosavedAt] = useState(null);
  const [showExitDialog, setShowExitDialog] = useState(false);
  const [errors, setErrors] = useState({});
  const [doctorInfo, setDoctorInfo] = useState(null);
  const [patientSuggestions, setPatientSuggestions] = useState([]);
  const [searchingPatients, setSearchingPatients] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState(null);
  const [patientReentryMessage, setPatientReentryMessage] = useState('');
  const autosaveConnectionWarnedRef = useRef(false);
  const hasLoadedInitialAutosaveRef = useRef(false);
  const lastAutosaveSignatureRef = useRef('');
  const exitBaselineSignatureRef = useRef('');
  const exitBaselinePayloadRef = useRef(null);
  const exitBaselineSessionExistsRef = useRef(false);
  const exitBaselineSessionKeyRef = useRef('');
  const [draftConsultationId, setDraftConsultationId] = useState(consultationId);

  // Session key para autoguardado
  const [sessionKey, setSessionKey] = useState(() => (
    draftSessionKey || (consultationId ? buildDraftSessionKey(consultationId) : `session_${user.id}_${Date.now()}`)
  ));

  // Estado del formulario
  const [formData, setFormData] = useState(() => createInitialFormData());

  const buildAutosavePayload = useCallback((consultationRecordId = draftConsultationId) => ({
    ...formData,
    current_step: currentStep,
    selected_patient_id: selectedPatientId,
    draft_consultation_id: consultationRecordId
  }), [formData, currentStep, selectedPatientId, draftConsultationId]);

  const buildAutosaveSignature = useCallback((payload, sessionKeyOverride = sessionKey) => (
    JSON.stringify({
      sessionKey: sessionKeyOverride,
      payload
    })
  ), [sessionKey]);

  const updateExitBaseline = useCallback((payload, {
    sessionExists = false,
    sessionKeyOverride = sessionKey
  } = {}) => {
    exitBaselinePayloadRef.current = payload;
    exitBaselineSessionExistsRef.current = sessionExists;
    exitBaselineSessionKeyRef.current = sessionKeyOverride;
    exitBaselineSignatureRef.current = buildAutosaveSignature(payload, sessionKeyOverride);
  }, [buildAutosaveSignature, sessionKey]);

  const persistAutosave = useCallback(async ({
    force = false,
    silent = false,
    sessionKeyOverride = sessionKey,
    consultationRecordId = draftConsultationId
  } = {}) => {
    const payload = buildAutosavePayload(consultationRecordId);
    const signature = buildAutosaveSignature(payload, sessionKeyOverride);

    if (!force && signature === lastAutosaveSignatureRef.current) {
      return {
        success: true,
        skipped: true,
        payload
      };
    }

    try {
      if (!silent) {
        setIsAutosaving(true);
        setAutosaveStatus('saving');
      }

      await consultationService.autosave(sessionKeyOverride, payload);
      autosaveConnectionWarnedRef.current = false;
      lastAutosaveSignatureRef.current = signature;
      setAutosaveStatus('saved');
      setLastAutosavedAt(new Date());

      return {
        success: true,
        payload
      };
    } catch (error) {
      const status = error?.response?.status;
      if (status === 530) {
        if (!autosaveConnectionWarnedRef.current) {
          console.warn('Autoguardado temporalmente no disponible (túnel/red).');
          autosaveConnectionWarnedRef.current = true;
        }

        if (!silent) {
          setAutosaveStatus('error');
        }

        return {
          success: false,
          deferred: true,
          payload
        };
      }

      if (!silent) {
        setAutosaveStatus('error');
      }

      console.error('Error en autoguardado:', error);
      return {
        success: false,
        error,
        payload
      };
    } finally {
      if (!silent) {
        setIsAutosaving(false);
      }
    }
  }, [buildAutosavePayload, buildAutosaveSignature, draftConsultationId, sessionKey]);

  useEffect(() => {
    if (!hasLoadedInitialAutosaveRef.current || !hasDraftableData(formData, currentStep, selectedPatientId)) {
      return undefined;
    }

    const debounce = setTimeout(() => {
      persistAutosave();
    }, 1800);

    return () => clearTimeout(debounce);
  }, [formData, currentStep, selectedPatientId, draftConsultationId, sessionKey, persistAutosave]);

  useEffect(() => {
    if (!hasLoadedInitialAutosaveRef.current || !hasDraftableData(formData, currentStep, selectedPatientId)) {
      return undefined;
    }

    const interval = setInterval(() => {
      persistAutosave({ force: true, silent: true });
    }, 30000);

    return () => clearInterval(interval);
  }, [formData, currentStep, selectedPatientId, draftConsultationId, sessionKey, persistAutosave]);

  // Recuperar sesión al montar
  useEffect(() => {
    if (hasLoadedInitialAutosaveRef.current) {
      return;
    }

    hasLoadedInitialAutosaveRef.current = true;

    const loadAutosave = async () => {
      try {
        if (consultationId) {
          const [autosaveResult, consultationResult] = await Promise.allSettled([
            consultationService.getAutosave(sessionKey),
            consultationService.getById(consultationId)
          ]);

          if (consultationResult.status === 'fulfilled' && consultationResult.value.success) {
            const consultationData = consultationResult.value.data;
            const consultationPatientId = consultationData.p_id || consultationData.paciente_id || null;
            setSelectedPatientId(consultationPatientId);

            if (!(autosaveResult.status === 'fulfilled' && autosaveResult.value.success && autosaveResult.value.data.form_data)) {
              const mappedFormData = mapConsultationToFormData(consultationData);
              const mappedStep = consultationData.pronostico ? 7 : 6;

              setFormData(mappedFormData);
              setCurrentStep(mappedStep);
              const mappedPayload = {
                ...mappedFormData,
                current_step: mappedStep,
                selected_patient_id: consultationPatientId,
                draft_consultation_id: consultationId
              };
              lastAutosaveSignatureRef.current = buildAutosaveSignature(mappedPayload);
              updateExitBaseline(mappedPayload, {
                sessionExists: false
              });
            }
          }

          if (autosaveResult.status === 'fulfilled' && autosaveResult.value.success && autosaveResult.value.data.form_data) {
            const recoveredFormData = normalizeLoadedFormData(autosaveResult.value.data.form_data);
            const recoveredPatientId = autosaveResult.value.data.form_data.selected_patient_id || null;
            const recoveredCurrentStep = Number(autosaveResult.value.data.form_data.current_step || 1);
            const recoveredDraftId = autosaveResult.value.data.form_data.draft_consultation_id || consultationId || null;

            setFormData(recoveredFormData);
            setSelectedPatientId(recoveredPatientId);
            setCurrentStep(recoveredCurrentStep);
            setDraftConsultationId(recoveredDraftId);
            setSaveMessage('Se recuperó un borrador guardado');
            setTimeout(() => setSaveMessage(''), 3000);
            const recoveredPayload = {
              ...recoveredFormData,
              current_step: recoveredCurrentStep,
              selected_patient_id: recoveredPatientId,
              draft_consultation_id: recoveredDraftId
            };
            lastAutosaveSignatureRef.current = buildAutosaveSignature(recoveredPayload);
            updateExitBaseline(recoveredPayload, {
              sessionExists: true
            });
          }

          return;
        }

        const result = await consultationService.getAutosave(sessionKey);
        if (result.success && result.data.form_data) {
          const recoveredFormData = normalizeLoadedFormData(result.data.form_data);
          const recoveredPatientId = result.data.form_data.selected_patient_id || null;
          const recoveredCurrentStep = Number(result.data.form_data.current_step || 1);
          const recoveredDraftId = result.data.form_data.draft_consultation_id || null;

          setFormData(recoveredFormData);
          setSelectedPatientId(recoveredPatientId);
          setCurrentStep(recoveredCurrentStep);
          setDraftConsultationId(recoveredDraftId);
          setSaveMessage('Se recuperó una sesión anterior');
          setTimeout(() => setSaveMessage(''), 3000);
          const recoveredPayload = {
            ...recoveredFormData,
            current_step: recoveredCurrentStep,
            selected_patient_id: recoveredPatientId,
            draft_consultation_id: recoveredDraftId
          };
          lastAutosaveSignatureRef.current = buildAutosaveSignature(recoveredPayload);
          updateExitBaseline(recoveredPayload, {
            sessionExists: true
          });
        } else {
          updateExitBaseline(buildAutosavePayload(null), {
            sessionExists: false
          });
        }
      } catch (error) {
        // No hay sesión guardada, está bien
      } finally {
        if (!exitBaselinePayloadRef.current) {
          updateExitBaseline(buildAutosavePayload(null), {
            sessionExists: false
          });
        }
        setAutosaveStatus('idle');
      }
    };
    loadAutosave();
  }, [buildAutosavePayload, buildAutosaveSignature, consultationId, sessionKey, updateExitBaseline]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Limpiar error del campo
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: null
      }));
    }
  };

  useEffect(() => {
    const query = formData.nombre.trim();

    if (query.length < 2) {
      setPatientSuggestions([]);
      return;
    }

    const timer = setTimeout(async () => {
      try {
        setSearchingPatients(true);
        const result = await patientService.searchByName(query, 8);
        setPatientSuggestions(result.data || []);
      } catch (error) {
        setPatientSuggestions([]);
      } finally {
        setSearchingPatients(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [formData.nombre]);

  const applyExistingPatient = (patient) => {
    setSelectedPatientId(patient.id);
    setPatientSuggestions([]);

    setFormData(prev => ({
      ...prev,
      nombre: patient.nombre || prev.nombre,
      genero: patient.genero || '',
      fecha_nacimiento: patient.fecha_nacimiento ? String(patient.fecha_nacimiento).slice(0, 10) : '',
      ocupacion: patient.ocupacion || '',
      domicilio: patient.domicilio || '',
      nombre_tutor: patient.nombre_tutor || '',
      antecedentes_familiares: patient.antecedentes_familiares || '',
      antecedentes_personales_no_patologicos: patient.antecedentes_personales_no_patologicos || '',
      antecedentes_personales_patologicos: patient.antecedentes_personales_patologicos || '',
      antecedentes_gineco_obstetricos: patient.antecedentes_gineco_obstetricos || ''
    }));

    if (patient.es_reingreso) {
      setPatientReentryMessage(`Paciente reingresante: ${patient.total_consultas || 0} consultas previas`);
    } else {
      setPatientReentryMessage('Paciente encontrado en base de datos');
    }
  };

  const handleCIE10Change = (selectedDiagnostics) => {
    setFormData(prev => ({
      ...prev,
      diagnostico_cie10: selectedDiagnostics
    }));
  };

  const handleMedChange = (index, field, value) => {
    setFormData(prev => {
      const meds = [...prev.medicamentos];
      meds[index] = { ...meds[index], [field]: value };
      return { ...prev, medicamentos: meds };
    });
  };

  const addMedicamento = () => {
    setFormData(prev => ({
      ...prev,
      medicamentos: [...prev.medicamentos, { nombre: '', presentacion: '', dosis: '', via: '', duracion: '', cantidad: '' }]
    }));
  };

  const removeMedicamento = (index) => {
    if (formData.medicamentos.length <= 1) return;
    setFormData(prev => ({
      ...prev,
      medicamentos: prev.medicamentos.filter((_, i) => i !== index)
    }));
  };

  const validateVitalSign = (field, value, errors) => {
    if (!value && value !== 0) {
      errors[field] = `${VITAL_RANGES[field]?.label || field} es requerido`;
      return;
    }
    const num = Number(value);
    if (Number.isNaN(num)) {
      errors[field] = 'Ingrese un valor numérico válido';
      return;
    }
    const range = VITAL_RANGES[field];
    if (range && (num < range.min || num > range.max)) {
      errors[field] = `Valor fuera de rango permitido (${range.min} – ${range.max} ${range.unit})`;
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      if (!formData.nombre?.trim()) newErrors.nombre = 'El nombre es requerido';
      if (!formData.genero) newErrors.genero = 'El género es requerido';
      if (!formData.fecha_nacimiento) newErrors.fecha_nacimiento = 'La fecha de nacimiento es requerida';

      // Vital signs with range validation
      validateVitalSign('peso', formData.peso, newErrors);
      validateVitalSign('talla', formData.talla, newErrors);
      if (!newErrors.talla && formData.talla) {
        const talla = Number(formData.talla);
        if (talla >= 3) {
          newErrors.talla = 'La talla debe ir en metros (ejemplo: 1.70), no en centímetros';
        }
      }
      validateVitalSign('temperatura', formData.temperatura, newErrors);
      validateVitalSign('tension_arterial_sistolica', formData.tension_arterial_sistolica, newErrors);
      validateVitalSign('tension_arterial_diastolica', formData.tension_arterial_diastolica, newErrors);
      validateVitalSign('frecuencia_cardiaca', formData.frecuencia_cardiaca, newErrors);
      validateVitalSign('frecuencia_respiratoria', formData.frecuencia_respiratoria, newErrors);

      // Diastólica must be < Sistólica
      if (formData.tension_arterial_sistolica && formData.tension_arterial_diastolica &&
          !newErrors.tension_arterial_sistolica && !newErrors.tension_arterial_diastolica) {
        if (Number(formData.tension_arterial_diastolica) >= Number(formData.tension_arterial_sistolica)) {
          newErrors.tension_arterial_diastolica = 'La diastólica debe ser menor que la sistólica';
        }
      }
    }

    if (step === 2 && !formData.motivo_consulta?.trim()) {
      newErrors.motivo_consulta = 'El motivo de consulta es requerido';
    }

    if (step === 3 && !formData.interrogatorio?.trim()) {
      newErrors.interrogatorio = 'El interrogatorio es requerido';
    }

    if (step === 4 && !formData.exploracion_fisica?.trim()) {
      newErrors.exploracion_fisica = 'La exploración física es requerida';
    }

    if (step === 5 && (!formData.diagnostico_cie10 || formData.diagnostico_cie10.length === 0)) {
      newErrors.diagnostico_cie10 = 'Seleccione al menos un diagnóstico CIE-10';
    }

    if (step === 6 && !formData.tratamiento?.trim()) {
      newErrors.tratamiento = 'El tratamiento es requerido';
    }

    if (step === 6 && formData.solicitar_laboratorio && !formData.solicitud_laboratorio?.trim()) {
      newErrors.solicitud_laboratorio = 'Describe qué laboratorios se van a solicitar';
    }

    if (step === 7 && !formData.pronostico?.trim()) {
      newErrors.pronostico = 'El pronóstico es requerido';
    }

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys(newErrors).length === 0;
  };

  const validateForm = () => {
    const stepsToValidate = [1, 2, 3, 4, 5, 6, 7];
    const aggregatedErrors = {};

    stepsToValidate.forEach((step) => {
      const currentErrors = {};

      if (step === 1) {
        if (!formData.nombre?.trim()) currentErrors.nombre = 'El nombre es requerido';
        if (!formData.genero) currentErrors.genero = 'El género es requerido';
        if (!formData.fecha_nacimiento) currentErrors.fecha_nacimiento = 'La fecha de nacimiento es requerida';

        // Vital signs with range validation
        const vitalFields = ['peso', 'talla', 'temperatura', 'tension_arterial_sistolica',
          'tension_arterial_diastolica', 'frecuencia_cardiaca', 'frecuencia_respiratoria'];
        vitalFields.forEach(field => validateVitalSign(field, formData[field], currentErrors));

        if (!currentErrors.talla && formData.talla && Number(formData.talla) >= 3) {
          currentErrors.talla = 'La talla debe ir en metros (ejemplo: 1.70), no en centímetros';
        }

        if (formData.tension_arterial_sistolica && formData.tension_arterial_diastolica &&
            !currentErrors.tension_arterial_sistolica && !currentErrors.tension_arterial_diastolica) {
          if (Number(formData.tension_arterial_diastolica) >= Number(formData.tension_arterial_sistolica)) {
            currentErrors.tension_arterial_diastolica = 'La diastólica debe ser menor que la sistólica';
          }
        }
      }

      if (step === 2 && !formData.motivo_consulta?.trim()) {
        currentErrors.motivo_consulta = 'El motivo de consulta es requerido';
      }

      if (step === 3 && !formData.interrogatorio?.trim()) {
        currentErrors.interrogatorio = 'El interrogatorio es requerido';
      }

      if (step === 4 && !formData.exploracion_fisica?.trim()) {
        currentErrors.exploracion_fisica = 'La exploración física es requerida';
      }

      if (step === 5 && (!formData.diagnostico_cie10 || formData.diagnostico_cie10.length === 0)) {
        currentErrors.diagnostico_cie10 = 'Seleccione al menos un diagnóstico CIE-10';
      }

      if (step === 6 && !formData.tratamiento?.trim()) {
        currentErrors.tratamiento = 'El tratamiento es requerido';
      }

      if (step === 6 && formData.solicitar_laboratorio && !formData.solicitud_laboratorio?.trim()) {
        currentErrors.solicitud_laboratorio = 'Describe qué laboratorios se van a solicitar';
      }

      if (step === 7 && !formData.pronostico?.trim()) {
        currentErrors.pronostico = 'El pronóstico es requerido';
      }

      Object.assign(aggregatedErrors, currentErrors);
    });

    setErrors(aggregatedErrors);
    return Object.keys(aggregatedErrors).length === 0;
  };

  // Map field names to human-readable step labels
  const fieldToStep = {
    nombre: 'Paso 1', genero: 'Paso 1', fecha_nacimiento: 'Paso 1',
    peso: 'Paso 1', talla: 'Paso 1', temperatura: 'Paso 1',
    tension_arterial_sistolica: 'Paso 1', tension_arterial_diastolica: 'Paso 1',
    frecuencia_cardiaca: 'Paso 1', frecuencia_respiratoria: 'Paso 1',
    motivo_consulta: 'Paso 2', interrogatorio: 'Paso 3',
    exploracion_fisica: 'Paso 4', diagnostico_cie10: 'Paso 5',
    tratamiento: 'Paso 6', solicitud_laboratorio: 'Paso 6', pronostico: 'Paso 7',
  };

  const buildConsultationData = (estado) => {
    const diagnosticoCodigos = (formData.diagnostico_cie10 || [])
      .map((diagnostico) => (typeof diagnostico === 'string' ? diagnostico : diagnostico.codigo))
      .filter(Boolean);

    const consultationData = {
      paciente: {
        id: selectedPatientId,
        nombre: formData.nombre,
        genero: formData.genero,
        fecha_nacimiento: formData.fecha_nacimiento,
        ocupacion: formData.ocupacion,
        domicilio: formData.domicilio,
        nombre_tutor: formData.nombre_tutor || null,
      },
      ...formData,
      diagnostico_cie10: diagnosticoCodigos,
      idx_diagnostico_presuntivo: diagnosticoCodigos[0] || '',
      estado
    };

    if (draftConsultationId) {
      consultationData.id = draftConsultationId;
    }

    return consultationData;
  };

  const notifyDraftsChanged = useCallback(() => {
    if (typeof onDraftsChanged === 'function') {
      onDraftsChanged();
    }
  }, [onDraftsChanged]);

  const syncDraftAutosave = async (consultationRecordId) => {
    if (!consultationRecordId) return;

    const stableSessionKey = buildDraftSessionKey(consultationRecordId);
    await persistAutosave({
      force: true,
      silent: true,
      sessionKeyOverride: stableSessionKey,
      consultationRecordId
    });

    if (sessionKey !== stableSessionKey) {
      try {
        await consultationService.deleteAutosave(sessionKey);
      } catch (error) {
        console.warn('No fue posible limpiar la sesión temporal previa:', error);
      }
      setSessionKey(stableSessionKey);
    }
  };

  const clearAutosaveSessions = async (consultationRecordId = null) => {
    const keysToDelete = new Set([sessionKey]);

    if (consultationRecordId) {
      keysToDelete.add(buildDraftSessionKey(consultationRecordId));
    }

    await Promise.all(
      Array.from(keysToDelete).map((key) => (
        consultationService.deleteAutosave(key).catch(() => null)
      ))
    );
  };

  const createOrUpdateDraftRecord = async () => {
    const result = await consultationService.create(buildConsultationData('borrador'));
    const savedDraftId = result.data?.consulta?.id || draftConsultationId;

    if (savedDraftId) {
      setDraftConsultationId(savedDraftId);
      await syncDraftAutosave(savedDraftId);
      updateExitBaseline(buildAutosavePayload(savedDraftId), {
        sessionExists: true,
        sessionKeyOverride: buildDraftSessionKey(savedDraftId)
      });
    }

    return savedDraftId;
  };

  const handleSaveDraft = async () => {
    setSaving(true);
    setSaveMessage('');

    try {
      await persistAutosave({ force: true });

      if (hasDraftPatientBasics(formData)) {
        await createOrUpdateDraftRecord();
        setSaveMessage('Borrador guardado exitosamente');
      } else {
        updateExitBaseline(buildAutosavePayload(), {
          sessionExists: true
        });
        setSaveMessage('Progreso guardado automáticamente. Complete nombre, género y fecha de nacimiento para registrar el borrador en el expediente.');
      }
      notifyDraftsChanged();
    } catch (error) {
      setSaveMessage('Error al guardar el borrador');
      console.error('Error guardando borrador:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setSaveMessage('__ERRORS__');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    setSaving(true);
    setSaveMessage('');

    try {
      const result = await consultationService.create(buildConsultationData('completada'));

      if (result.success) {
        setSavedConsultation(result.data);
        setSaveMessage('Consulta guardada exitosamente');
        
        // Eliminar autoguardado
        await clearAutosaveSessions(result.data?.consulta?.id || draftConsultationId || consultationId);
        notifyDraftsChanged();
        
        // Obtener información completa del doctor (con cédula) para la receta
        const doctorData = await authService.getDoctorInfo();
        setDoctorInfo(doctorData.data);
        
        // Mostrar opción de receta
        setTimeout(() => {
          const showPrescription = window.confirm(
            '¿Desea generar una receta para este paciente?'
          );
          if (showPrescription) {
            setShowReceta(true);
          } else {
            onSuccess();
          }
        }, 1000);
      }
    } catch (error) {
      setSaveMessage('Error al guardar la consulta');
      console.error('Error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleRequestExit = () => {
    const currentPayload = buildAutosavePayload();
    const currentSignature = buildAutosaveSignature(currentPayload);

    if (
      !hasDraftableData(formData, currentStep, selectedPatientId) ||
      currentSignature === exitBaselineSignatureRef.current
    ) {
      onBack();
      return;
    }

    setShowExitDialog(true);
  };

  const handleExitWithoutSaving = async () => {
    setSaving(true);

    try {
      if (exitBaselineSessionExistsRef.current && exitBaselinePayloadRef.current) {
        const baselineSessionKey = exitBaselineSessionKeyRef.current || sessionKey;
        await consultationService.autosave(baselineSessionKey, exitBaselinePayloadRef.current).catch(() => null);
        lastAutosaveSignatureRef.current = buildAutosaveSignature(
          exitBaselinePayloadRef.current,
          baselineSessionKey
        );
      } else {
        await consultationService.deleteAutosave(sessionKey).catch(() => null);
        lastAutosaveSignatureRef.current = '';
      }

      notifyDraftsChanged();
      setShowExitDialog(false);
      onBack();
    } finally {
      setSaving(false);
    }
  };

  const handleSaveDraftAndExit = async () => {
    setSaving(true);
    setSaveMessage('');

    try {
      await persistAutosave({ force: true });

      if (hasDraftPatientBasics(formData)) {
        await createOrUpdateDraftRecord();
      }

      notifyDraftsChanged();
      setShowExitDialog(false);
      onBack();
    } catch (error) {
      setSaveMessage('Error al guardar el borrador antes de salir');
      console.error('Error al guardar el borrador al salir:', error);
    } finally {
      setSaving(false);
    }
  };

  const nextStep = () => {
    if (currentStep < 7) {
      if (!validateStep(currentStep)) {
        setSaveMessage('__ERRORS__');
        window.scrollTo({ top: 0, behavior: 'smooth' });
        return;
      }
      setSaveMessage('');
      setCurrentStep(currentStep + 1);
      window.scrollTo(0, 0);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      window.scrollTo(0, 0);
    }
  };

  // Check if a step has validation issues (for step indicator warnings)
  const stepHasErrors = (stepNum) => {
    const stepFields = {
      1: ['nombre', 'genero', 'fecha_nacimiento', 'peso', 'talla', 'temperatura',
          'tension_arterial_sistolica', 'tension_arterial_diastolica', 'frecuencia_cardiaca', 'frecuencia_respiratoria'],
        2: ['motivo_consulta'],
        3: ['interrogatorio'],
        4: ['exploracion_fisica'],
        5: ['diagnostico_cie10'],
        6: ['tratamiento', 'solicitud_laboratorio'],
        7: ['pronostico'],
    };
    const fields = stepFields[stepNum] || [];
    return fields.some(f => errors[f]);
  };

  const getVitalInputClass = (field) => {
    const status = getVitalStatus(field, formData[field]);
    if (errors[field]) return 'error';
    if (status === 'warning') return 'vital-warning';
    if (status === 'impossible') return 'error';
    return '';
  };

  const renderVitalHelper = (field) => {
    const range = VITAL_RANGES[field];
    if (!range) return null;
    const status = getVitalStatus(field, formData[field]);
    return (
      <>
        {errors[field] && <span className="error-text">{errors[field]}</span>}
        {status === 'warning' && !errors[field] && (
          <span className="warning-text">Valor inusual (normal: {range.normalMin}–{range.normalMax} {range.unit})</span>
        )}
        <small className="vital-range-hint">Rango: {range.min}–{range.max} {range.unit} · Normal: {range.normalMin}–{range.normalMax}</small>
      </>
    );
  };

  const renderStepIndicator = () => {
    const steps = [
      'ID + Signos + Antecedentes',
      'Motivo',
      'Interrogatorio',
      'Exploración',
      'Diagnóstico',
      'Tratamiento',
      'Pronóstico'
    ];

    return (
      <div className="step-indicator">
        {steps.map((step, index) => {
          const hasError = stepHasErrors(index + 1);
          return (
            <div
              key={index}
              className={`step ${currentStep === index + 1 ? 'active' : ''} ${
                currentStep > index + 1 ? 'completed' : ''
              } ${hasError && currentStep !== index + 1 ? 'has-error' : ''}`}
            >
              <div className="step-number">
                {hasError && currentStep > index + 1 ? '!' : index + 1}
              </div>
              <div className="step-label">{step}</div>
            </div>
          );
        })}
      </div>
    );
  };

  const autosaveLabel = (() => {
    if (isAutosaving || autosaveStatus === 'saving') {
      return 'Guardando borrador...';
    }

    if (autosaveStatus === 'error') {
      return 'Autoguardado pendiente';
    }

    if (lastAutosavedAt) {
      return `Autoguardado ${formatAutosaveTimestamp(lastAutosavedAt)}`;
    }

    return 'Borrador en progreso';
  })();

  const isEditingDraft = Boolean(consultationId || draftSessionKey || draftConsultationId);

  if (showReceta && savedConsultation && doctorInfo) {
    return (
      <RecetaPreview
        consulta={{ 
          ...formData, 
          doctor: {
            nombre: doctorInfo.nombre,
            cedula: doctorInfo.cedula,
            universidad: doctorInfo.universidad || '',
            logo_url: doctorInfo.logo_url || ''
          }
        }}
        onClose={() => {
          setShowReceta(false);
          onSuccess();
        }}
      />
    );
  }

  return (
    <div className="patient-form-container">
      <div className="form-header">
        <button onClick={handleRequestExit} className="btn btn-secondary no-print">
          ← {backLabel}
        </button>
        <div className="form-header-main">
          <h2>{isEditingDraft ? 'Continuar Borrador de Consulta' : 'Nueva Consulta de Paciente'}</h2>
          {hasDraftableData(formData, currentStep, selectedPatientId) && (
            <div className="draft-status-row">
              <span className={`autosave-chip autosave-chip--${autosaveStatus}`}>
                {autosaveLabel}
              </span>
              {draftConsultationId && (
                <span className="draft-status-chip">
                  Borrador activo
                </span>
              )}
            </div>
          )}
        </div>
        {saveMessage && saveMessage !== '__ERRORS__' && (
          <div className={`save-message ${saveMessage.includes('Error') ? 'error' : 'success'}`}>
            {saveMessage}
          </div>
        )}
        {saveMessage === '__ERRORS__' && Object.keys(errors).length > 0 && (
          <div className="save-message error error-summary">
            <strong>Corrija los siguientes errores antes de guardar:</strong>
            <ol className="error-list">
              {Object.entries(errors).filter(([,v]) => v).map(([field, msg], i) => (
                <li key={field}>
                  <span className="error-step">{fieldToStep[field] || ''}</span> {msg}
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>

      {showExitDialog && (
        <div className="draft-dialog-backdrop" role="presentation">
          <div className="draft-dialog" role="dialog" aria-modal="true" aria-labelledby="draft-exit-title">
            <h3 id="draft-exit-title">¿Desea guardar este borrador antes de salir?</h3>
            <p>
              Hay cambios registrados hasta el paso {currentStep}. Puede guardar el avance actual,
              salir sin conservarlo o continuar editando la consulta.
            </p>
            <div className="draft-dialog-actions">
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSaveDraftAndExit}
                disabled={saving}
              >
                {saving ? 'Guardando...' : 'Guardar borrador y salir'}
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleExitWithoutSaving}
                disabled={saving}
              >
                Salir sin guardar
              </button>
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setShowExitDialog(false)}
                disabled={saving}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {renderStepIndicator()}

      <div className="form-content">
        {/* PASO 1: Ficha de ID + Signos Vitales */}
        {currentStep === 1 && (
          <div className="form-section">
            <h3>3.1 - Ficha de Identificación, Signos Vitales y Antecedentes</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label>Nombre Completo <span className="required">*</span></label>
                <input
                  type="text"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  className={errors.nombre ? 'error' : ''}
                  required
                />
                {errors.nombre && <span className="error-text">{errors.nombre}</span>}
                {searchingPatients && <small>Buscando paciente en base de datos...</small>}
                {patientReentryMessage && <small className="reentry-message">{patientReentryMessage}</small>}
                {patientSuggestions.length > 0 && (
                  <div className="patient-suggestions">
                    {patientSuggestions.map((patient) => (
                      <button
                        type="button"
                        key={patient.id}
                        className="patient-suggestion-item"
                        onClick={() => applyExistingPatient(patient)}
                      >
                        <span>{patient.nombre}</span>
                        <small>
                          {patient.es_reingreso ? `Reingreso · ${patient.total_consultas} consultas` : 'Paciente existente'}
                        </small>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Género <span className="required">*</span></label>
                <select
                  name="genero"
                  value={formData.genero}
                  onChange={handleChange}
                  className={errors.genero ? 'error' : ''}
                  required
                >
                  <option value="">Seleccione...</option>
                  <option value="Masculino">Masculino</option>
                  <option value="Femenino">Femenino</option>
                </select>
                {errors.genero && <span className="error-text">{errors.genero}</span>}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Fecha de Nacimiento <span className="required">*</span></label>
                <input
                  type="date"
                  name="fecha_nacimiento"
                  value={formData.fecha_nacimiento}
                  onChange={handleChange}
                  className={errors.fecha_nacimiento ? 'error' : ''}
                  required
                />
                {errors.fecha_nacimiento && <span className="error-text">{errors.fecha_nacimiento}</span>}
              </div>

              <div className="form-group">
                <label>Ocupación</label>
                <input
                  type="text"
                  name="ocupacion"
                  value={formData.ocupacion}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Domicilio</label>
              <input
                type="text"
                name="domicilio"
                value={formData.domicilio}
                onChange={handleChange}
              />
            </div>

            <div className="form-group">
              <label>Nombre del Padre o Tutor (opcional, para menores)</label>
              <input
                type="text"
                name="nombre_tutor"
                value={formData.nombre_tutor}
                onChange={handleChange}
              />
            </div>

            <hr />
            <h4>Signos Vitales</h4>

            <div className="form-row form-row-4">
              <div className="form-group">
                <label>Peso (kg) <span className="required">*</span></label>
                <input
                  type="number"
                  step="0.01"
                  min="0.5"
                  max="300"
                  name="peso"
                  value={formData.peso}
                  onChange={handleChange}
                  className={getVitalInputClass('peso')}
                  placeholder="Ej: 70"
                />
                {renderVitalHelper('peso')}
              </div>

              <div className="form-group">
                <label>Talla (m) <span className="required">*</span></label>
                <input
                  type="number"
                  step="0.01"
                  min="0.3"
                  max="3.0"
                  name="talla"
                  value={formData.talla}
                  onChange={handleChange}
                  className={getVitalInputClass('talla')}
                  placeholder="Ej: 1.70"
                />
                {renderVitalHelper('talla')}
                {formData.peso && formData.talla && Number(formData.talla) > 0 && (
                  <small className="imc-display">
                    IMC: {(Number(formData.peso) / (Number(formData.talla) ** 2)).toFixed(1)} kg/m²
                  </small>
                )}
              </div>

              <div className="form-group">
                <label>Temperatura (°C) <span className="required">*</span></label>
                <input
                  type="number"
                  step="0.1"
                  min="34"
                  max="42"
                  name="temperatura"
                  value={formData.temperatura}
                  onChange={handleChange}
                  className={getVitalInputClass('temperatura')}
                  placeholder="Ej: 36.5"
                />
                {renderVitalHelper('temperatura')}
              </div>

              <div className="form-group">
                <label>T/A Sistólica (mmHg) <span className="required">*</span></label>
                <input
                  type="number"
                  min="60"
                  max="300"
                  name="tension_arterial_sistolica"
                  value={formData.tension_arterial_sistolica}
                  onChange={handleChange}
                  className={getVitalInputClass('tension_arterial_sistolica')}
                  placeholder="Ej: 120"
                />
                {renderVitalHelper('tension_arterial_sistolica')}
              </div>
            </div>

            <div className="form-row form-row-3">
              <div className="form-group">
                <label>T/A Diastólica (mmHg) <span className="required">*</span></label>
                <input
                  type="number"
                  min="30"
                  max="200"
                  name="tension_arterial_diastolica"
                  value={formData.tension_arterial_diastolica}
                  onChange={handleChange}
                  className={getVitalInputClass('tension_arterial_diastolica')}
                  placeholder="Ej: 80"
                />
                {renderVitalHelper('tension_arterial_diastolica')}
              </div>

              <div className="form-group">
                <label>Frecuencia Cardiaca (lpm) <span className="required">*</span></label>
                <input
                  type="number"
                  min="30"
                  max="250"
                  name="frecuencia_cardiaca"
                  value={formData.frecuencia_cardiaca}
                  onChange={handleChange}
                  className={getVitalInputClass('frecuencia_cardiaca')}
                  placeholder="Ej: 72"
                />
                {renderVitalHelper('frecuencia_cardiaca')}
              </div>

              <div className="form-group">
                <label>Frecuencia Respiratoria (rpm) <span className="required">*</span></label>
                <input
                  type="number"
                  min="8"
                  max="60"
                  name="frecuencia_respiratoria"
                  value={formData.frecuencia_respiratoria}
                  onChange={handleChange}
                  className={getVitalInputClass('frecuencia_respiratoria')}
                  placeholder="Ej: 16"
                />
                {renderVitalHelper('frecuencia_respiratoria')}
              </div>
            </div>

            <hr />
            <h4>Información Adicional</h4>

            <div className="form-row">
              <div className="form-group">
                <label>Aseguradora</label>
                <select
                  name="aseguradora"
                  value={formData.aseguradora}
                  onChange={handleChange}
                >
                  <option value="">Seleccione...</option>
                  <option value="Qualitas">Qualitas</option>
                  <option value="AXA">AXA</option>
                  <option value="Sin Seguro">Sin Seguro</option>
                  <option value="Otra">Otra</option>
                </select>
              </div>

              <div className="form-group">
                <label>Medio de Llegada</label>
                <input
                  type="text"
                  name="medio_llegada"
                  value={formData.medio_llegada}
                  onChange={handleChange}
                  placeholder="Ej: Ambulancia, auto particular, etc."
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Fecha de Ingreso (dejar en blanco para usar hora actual)</label>
                <input
                  type="datetime-local"
                  name="fecha_ingreso"
                  value={formData.fecha_ingreso}
                  onChange={handleChange}
                />
              </div>
            </div>

            {formData.genero === 'Femenino' && (
              <>
                <hr />
                <div className="form-group">
                  <label>Antecedentes Gineco-Obstétricos</label>
                  <textarea
                    name="antecedentes_gineco_obstetricos"
                    value={formData.antecedentes_gineco_obstetricos}
                    onChange={handleChange}
                    rows="4"
                    placeholder="Gestas, partos, cesáreas, abortos, FUM, etc."
                  />
                </div>
              </>
            )}

            <hr />
            <h4>Antecedentes</h4>

            <div className="form-group">
              <label>Antecedentes Familiares (máximo 400 palabras)</label>
              <textarea
                name="antecedentes_familiares"
                value={formData.antecedentes_familiares}
                onChange={handleChange}
                rows="5"
                placeholder="Diabetes, hipertensión, cáncer, etc. en familiares..."
              />
              <small>{formData.antecedentes_familiares.split(' ').filter(w => w).length} palabras</small>
            </div>

            <div className="form-group">
              <label>Antecedentes Personales No Patológicos (máximo 400 palabras)</label>
              <textarea
                name="antecedentes_personales_no_patologicos"
                value={formData.antecedentes_personales_no_patologicos}
                onChange={handleChange}
                rows="5"
                placeholder="Alimentación, higiene, actividad física, etc..."
              />
              <small>{formData.antecedentes_personales_no_patologicos.split(' ').filter(w => w).length} palabras</small>
            </div>

            <div className="form-group">
              <label>Antecedentes Personales Patológicos (máximo 400 palabras)</label>
              <textarea
                name="antecedentes_personales_patologicos"
                value={formData.antecedentes_personales_patologicos}
                onChange={handleChange}
                rows="5"
                placeholder="Enfermedades previas, cirugías, alergias, medicamentos actuales..."
              />
              <small>{formData.antecedentes_personales_patologicos.split(' ').filter(w => w).length} palabras</small>
            </div>
          </div>
        )}

        {/* PASO 3: Motivo de Consulta */}
        {currentStep === 2 && (
          <div className="form-section">
            <h3>3.3 - Motivo de la Consulta</h3>
            
            <div className="form-group">
              <label>¿Qué pasó? <span className="required">*</span> (máximo 400 palabras)</label>
              <textarea
                name="motivo_consulta"
                value={formData.motivo_consulta}
                onChange={handleChange}
                rows="6"
                className={errors.motivo_consulta ? 'error' : ''}
                placeholder="Descripción del padecimiento actual..."
                required
              />
              {errors.motivo_consulta && <span className="error-text">{errors.motivo_consulta}</span>}
              <small>{formData.motivo_consulta.split(' ').filter(w => w).length} palabras</small>
            </div>
          </div>
        )}

        {/* PASO 4: Interrogatorio */}
        {currentStep === 3 && (
          <div className="form-section">
            <h3>3.4 - Interrogatorio</h3>
            
            <div className="form-group">
              <label>Interrogatorio por Aparatos y Sistemas <span className="required">*</span> (máximo 1000 palabras)</label>
              <textarea
                name="interrogatorio"
                value={formData.interrogatorio}
                onChange={handleChange}
                rows="10"
                className={errors.interrogatorio ? 'error' : ''}
                placeholder="Interrogatorio detallado..."
                required
              />
              {errors.interrogatorio && <span className="error-text">{errors.interrogatorio}</span>}
              <small>{formData.interrogatorio.split(' ').filter(w => w).length} palabras</small>
            </div>
          </div>
        )}

        {/* PASO 5: Exploración Física */}
        {currentStep === 4 && (
          <div className="form-section">
            <h3>3.5 - Exploración Física</h3>
            
            <div className="form-group">
              <label>Exploración Física <span className="required">*</span> (máximo 1000 palabras)</label>
              <textarea
                name="exploracion_fisica"
                value={formData.exploracion_fisica}
                onChange={handleChange}
                rows="10"
                className={errors.exploracion_fisica ? 'error' : ''}
                placeholder="Hallazgos de la exploración física..."
                required
              />
              {errors.exploracion_fisica && <span className="error-text">{errors.exploracion_fisica}</span>}
              <small>{formData.exploracion_fisica.split(' ').filter(w => w).length} palabras</small>
            </div>
          </div>
        )}

        {/* PASO 6: Diagnóstico */}
        {currentStep === 5 && (
          <div className="form-section">
            <h3>3.6 - Diagnóstico Clínico (CIE-10)</h3>
            
            <CIE10Selector
              selectedDiagnostics={formData.diagnostico_cie10}
              onChange={handleCIE10Change}
            />
            {errors.diagnostico_cie10 && <span className="error-text">{errors.diagnostico_cie10}</span>}
          </div>
        )}

        {/* PASO 7: Tratamiento */}
        {currentStep === 6 && (
          <div className="form-section">
            <h3>3.7 - Terapéutica Empleada y Resultados de Laboratorio</h3>
            
            <div className="form-group">
              <label>Tratamiento <span className="required">*</span></label>
              <textarea
                name="tratamiento"
                value={formData.tratamiento}
                onChange={handleChange}
                rows="6"
                className={errors.tratamiento ? 'error' : ''}
                placeholder="Medicamentos, dosis, vía de administración, duración..."
                required
              />
              {errors.tratamiento && <span className="error-text">{errors.tratamiento}</span>}
            </div>

            <hr />
            <h4>Medicamentos para Receta</h4>

            <div className="form-group">
              <label>IDX (Diagnóstico presuntivo)</label>
              <input
                type="text"
                value={
                  formData.diagnostico_cie10 && formData.diagnostico_cie10.length > 0
                    ? formData.diagnostico_cie10.map(d => `${d.codigo} - ${d.descripcion}`).join(' | ')
                    : 'Seleccione diagnóstico CIE-10 en el paso 6'
                }
                disabled
              />
            </div>

            {formData.medicamentos.map((med, idx) => (
              <div key={idx} className="medicamento-card">
                <div className="medicamento-header">
                  <strong>Medicamento {idx + 1}</strong>
                  {formData.medicamentos.length > 1 && (
                    <button type="button" className="btn-remove-med" onClick={() => removeMedicamento(idx)}>Eliminar</button>
                  )}
                </div>
                <div className="form-row form-row-3">
                  <div className="form-group">
                    <label>Denominación genérica</label>
                    <input type="text" value={med.nombre} onChange={(e) => handleMedChange(idx, 'nombre', e.target.value)} placeholder="Ej: Paracetamol" />
                  </div>
                  <div className="form-group">
                    <label>Presentación</label>
                    <input type="text" value={med.presentacion} onChange={(e) => handleMedChange(idx, 'presentacion', e.target.value)} placeholder="Ej: Tabletas" />
                  </div>
                  <div className="form-group">
                    <label>Dosis / Gramaje</label>
                    <input type="text" value={med.dosis} onChange={(e) => handleMedChange(idx, 'dosis', e.target.value)} placeholder="Ej: 500mg" />
                  </div>
                </div>
                <div className="form-row form-row-3">
                  <div className="form-group">
                    <label>Vía de administración</label>
                    <input type="text" value={med.via} onChange={(e) => handleMedChange(idx, 'via', e.target.value)} placeholder="Ej: Oral" />
                  </div>
                  <div className="form-group">
                    <label>Duración</label>
                    <input type="text" value={med.duracion} onChange={(e) => handleMedChange(idx, 'duracion', e.target.value)} placeholder="Ej: 5 días" />
                  </div>
                  <div className="form-group">
                    <label>Cantidad</label>
                    <input type="text" value={med.cantidad} onChange={(e) => handleMedChange(idx, 'cantidad', e.target.value)} placeholder="Ej: 10" />
                  </div>
                </div>
              </div>
            ))}
            <button type="button" className="btn btn-secondary btn-add-med" onClick={addMedicamento}>
              + Agregar otro medicamento
            </button>

            <div className="form-group">
              <label>Medicamento controlado</label>
              <select
                name="receta_medicamento_controlado"
                value={formData.receta_medicamento_controlado}
                onChange={handleChange}
              >
                <option value="">Seleccione...</option>
                <option value="No">No</option>
                <option value="Si">Sí</option>
              </select>
            </div>

            <div className="form-group">
              <label>Domicilio para medicamento controlado (opcional)</label>
              <input
                type="text"
                name="receta_domicilio_controlado"
                value={formData.receta_domicilio_controlado}
                onChange={handleChange}
                placeholder="Completar cuando aplique"
              />
            </div>

            <div className="form-group">
              <label>Resultados de Laboratorio</label>
              <textarea
                name="resultados_laboratorio"
                value={formData.resultados_laboratorio}
                onChange={handleChange}
                rows="4"
                placeholder="Resultados de estudios de laboratorio o gabinete..."
              />
            </div>

            <div className="form-group">
              <label>
                <input
                  type="checkbox"
                  name="solicitar_laboratorio"
                  checked={!!formData.solicitar_laboratorio}
                  onChange={handleChange}
                  style={{ marginRight: '8px' }}
                />
                Solicitar laboratorios para este paciente
              </label>
            </div>

            {formData.solicitar_laboratorio && (
              <div className="form-group">
                <label>Solicitud de laboratorio <span className="required">*</span></label>
                <textarea
                  name="solicitud_laboratorio"
                  value={formData.solicitud_laboratorio}
                  onChange={handleChange}
                  rows="4"
                  className={errors.solicitud_laboratorio ? 'error' : ''}
                  placeholder="Ejemplo: BH completa, química sanguínea de 6 elementos, examen general de orina, perfil lipídico y HbA1c."
                />
                {errors.solicitud_laboratorio && <span className="error-text">{errors.solicitud_laboratorio}</span>}
              </div>
            )}
          </div>
        )}

        {/* PASO 8: Pronóstico */}
        {currentStep === 7 && (
          <div className="form-section">
            <h3>3.8 - Pronóstico</h3>
            
            <div className="form-group">
              <label>Pronóstico (máximo 400 palabras)</label>
              <textarea
                name="pronostico"
                value={formData.pronostico}
                onChange={handleChange}
                rows="5"
                className={errors.pronostico ? 'error' : ''}
                placeholder="Pronóstico del paciente..."
              />
              {errors.pronostico && <span className="error-text">{errors.pronostico}</span>}
              <small>{formData.pronostico.split(' ').filter(w => w).length} palabras</small>
            </div>

            <div className="firma-section">
              <h4>Firma del Doctor</h4>
              <p><strong>Nombre:</strong> {user.nombre}</p>
              <p><em>(La cédula profesional se incluirá automáticamente en la receta)</em></p>
            </div>
          </div>
        )}
      </div>

      <div className="form-navigation">
        {currentStep > 1 && (
          <button onClick={prevStep} className="btn btn-secondary">
            ← Anterior
          </button>
        )}

        <div className="nav-spacer"></div>

        {currentStep >= 6 && (
          <button
            type="button"
            onClick={handleSaveDraft}
            className="btn btn-secondary"
            disabled={saving}
          >
            {saving ? 'Guardando...' : 'Guardar borrador'}
          </button>
        )}

        {currentStep < 7 && (
          <button onClick={nextStep} className="btn btn-primary">
            Siguiente →
          </button>
        )}

            {currentStep === 7 && (
          <button
            onClick={handleSubmit}
            className="btn btn-success"
            disabled={saving}
          >
            {saving ? 'Guardando...' : 'Guardar Consulta'}
          </button>
        )}
      </div>
    </div>
  );
};

export default PatientForm;
